// Parses and validates the ${element.property} references used in report data
// element MATH / logic return properties, e.g.
//   total=MATH(${dr_composite.total}+${dr_amalgam.total})
//   score=MATH(POSITION(allstudentsreport.total_procedures_live,students.Year))
// The rules mirror ReportTemplate::replaceVariable and ReportDataElement::tryDoLogicFilters:
// "${scope.name}" reads another data element, "${name}" reads this element's own property,
// and an unknown element or property silently evaluates to 0 - hence these checks.

const VARIABLE = /\$\{([A-Za-z0-9_\-.]+)\}/g;
// Stats / totals functions take bare "element.property" arguments (ReportTemplatePatterns::$stats / $totals)
const STATS_FUNCTION = /\b(POSITION|MIN|MAX|MEDIAN|MEAN|QUARTILE|STDEV|TOTAL)\(([^()]*)\)/g;
const BARE_REFERENCE = /^[A-Za-z_][A-Za-z0-9_-]*\.[A-Za-z0-9_-]+$/;
const AUTOCOMPLETE_CONTEXT = /\$\{([A-Za-z0-9_-]*)(?:\.([A-Za-z0-9_-]*))?$/;
const MAX_COMPLETIONS = 12;

export function getPropertyName(value) {
	return (value || '').split('=')[0].trim();
}

/**
 * Build a name => { name, caption, properties: Set } lookup from the propertyreference
 * endpoint, overriding the element being edited with its live (possibly unsaved) state.
 */
export function buildIndex(dataelements, self = null) {
	const index = new Map();
	for (const element of dataelements || []) {
		index.set(element.name, {
			name: element.name,
			caption: element.caption,
			properties: new Set(element.properties || []),
		});
	}
	if (self && self.name) {
		index.set(self.name, {
			name: self.name,
			caption: self.caption,
			properties: new Set(self.properties || []),
		});
	}
	return index;
}

/**
 * Every element/property reference in a formula, with its position in the text.
 */
export function findReferences(formula) {
	const references = [];
	if (!formula) {
		return references;
	}

	for (const match of formula.matchAll(VARIABLE)) {
		const parts = match[1].split('.');
		references.push({
			raw: match[0],
			start: match.index,
			scope: parts.length === 2 ? parts[0] : null,
			property: parts.length === 2 ? parts[1] : parts[0],
			malformed: parts.length > 2 || parts.some(part => part === ''),
		});
	}

	for (const match of formula.matchAll(STATS_FUNCTION)) {
		let offset = match.index + match[1].length + 1;
		for (const arg of match[2].split(',')) {
			const token = arg.trim();
			if (BARE_REFERENCE.test(token)) {
				const [scope, property] = token.split('.');
				references.push({
					raw: token,
					start: offset + arg.indexOf(token),
					scope,
					property,
					malformed: false,
				});
			}
			offset += arg.length + 1;
		}
	}

	return references.sort((a, b) => a.start - b.start);
}

function levenshtein(a, b) {
	const previous = Array.from({ length: b.length + 1 }, (_, i) => i);
	for (let i = 1; i <= a.length; i++) {
		let diagonal = previous[0];
		previous[0] = i;
		for (let j = 1; j <= b.length; j++) {
			const above = previous[j];
			previous[j] = Math.min(
				previous[j] + 1,
				previous[j - 1] + 1,
				diagonal + (a[i - 1] === b[j - 1] ? 0 : 1)
			);
			diagonal = above;
		}
	}
	return previous[b.length];
}

/**
 * The closest candidate to a mistyped name, or null if nothing is reasonably close.
 */
export function closestMatch(name, candidates) {
	const lower = name.toLowerCase();
	let best = null;
	let bestDistance = Infinity;
	for (const candidate of candidates) {
		const distance = levenshtein(lower, candidate.toLowerCase());
		if (distance < bestDistance) {
			best = candidate;
			bestDistance = distance;
		}
	}
	const threshold = Math.max(2, Math.floor(name.length / 3));
	return bestDistance <= threshold ? best : null;
}

/**
 * Warnings for a custom return property value such as "total=MATH(${a.b}+${c})".
 *
 * self: { name, properties } of the element being edited - used for "${property}"
 *       references and references to the element by its own name.
 * existingNames: property names already on the element, to catch duplicates when adding.
 *
 * Each warning is { type, raw, scope, property, suggestion }.
 */
export function validateCustomProperty(value, index, { self = null, existingNames = null } = {}) {
	const warnings = [];
	if (!value || !value.trim()) {
		return warnings;
	}

	if (!value.includes('=')) {
		warnings.push({ type: 'missing-name', raw: value.trim() });
	} else {
		const name = getPropertyName(value);
		if (!name) {
			warnings.push({ type: 'missing-name', raw: value.trim() });
		} else if (existingNames && existingNames.includes(name)) {
			warnings.push({ type: 'duplicate-name', raw: name, property: name });
		}
	}

	const formula = value.includes('=') ? value.substring(value.indexOf('=') + 1) : value;
	const selfProperties = self && self.properties ? self.properties : [];

	for (const reference of findReferences(formula)) {
		if (reference.malformed) {
			warnings.push({ type: 'malformed', raw: reference.raw });
			continue;
		}

		if (reference.scope === null) {
			if (!selfProperties.includes(reference.property)) {
				warnings.push({
					type: 'unknown-self-property',
					raw: reference.raw,
					property: reference.property,
					suggestion: closestMatch(reference.property, selfProperties),
				});
			}
			continue;
		}

		const element = index.get(reference.scope);
		if (!element) {
			warnings.push({
				type: 'unknown-element',
				raw: reference.raw,
				scope: reference.scope,
				property: reference.property,
				suggestion: closestMatch(reference.scope, [...index.keys()]),
			});
			continue;
		}

		if (!element.properties.has(reference.property)) {
			warnings.push({
				type: 'unknown-property',
				raw: reference.raw,
				scope: reference.scope,
				property: reference.property,
				suggestion: closestMatch(reference.property, [...element.properties]),
			});
		}
	}

	return warnings;
}

/**
 * If the caret sits inside an unfinished "${scope" or "${scope.prop", return what is being
 * typed and where the replacement starts; otherwise null.
 */
export function getAutocompleteContext(text, caret) {
	const before = text.substring(0, caret);
	const match = before.match(AUTOCOMPLETE_CONTEXT);
	if (!match) {
		return null;
	}
	if (match[2] === undefined) {
		return { scope: null, partial: match[1], from: caret - match[1].length };
	}
	return { scope: match[1], partial: match[2], from: caret - match[2].length };
}

function rankByPartial(items, partial, getKey) {
	const lower = partial.toLowerCase();
	const prefix = [];
	const contains = [];
	for (const item of items) {
		const key = getKey(item).toLowerCase();
		if (key.startsWith(lower)) {
			prefix.push(item);
		} else if (lower && key.includes(lower)) {
			contains.push(item);
		}
	}
	return prefix.concat(contains).slice(0, MAX_COMPLETIONS);
}

/**
 * Completions for an autocomplete context. Each is { label, detail, insert, closes }:
 * "insert" replaces the partial text, "closes" says whether it completes the reference.
 */
export function getCompletions(context, index, self = null) {
	if (!context) {
		return [];
	}

	if (context.scope !== null) {
		const element = index.get(context.scope);
		if (!element) {
			return [];
		}
		return rankByPartial([...element.properties], context.partial, p => p).map(property => ({
			label: property,
			detail: null,
			insert: `${property}}`,
			closes: true,
		}));
	}

	const ownProperties = (self && self.properties ? self.properties : []).map(property => ({
		label: property,
		detail: 'this-element',
		insert: `${property}}`,
		closes: true,
	}));
	const elements = [...index.values()].map(element => ({
		label: element.name,
		detail: element.caption,
		insert: `${element.name}.`,
		closes: false,
	}));
	return rankByPartial(elements.concat(ownProperties), context.partial, c => c.label);
}
